'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { T } from '@/lib/tokens';
import { Orb } from '@/components/interview/Orb';
import { useRealtimeSession, TranscriptEntry } from '@/hooks/useRealtimeSession';

// ── Shared atoms ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, { bg: string; text: string }> = {
    idle:       { bg: T.creamMid, text: T.espressoLight },
    connecting: { bg: 'oklch(92% 0.04 65)', text: 'oklch(48% 0.14 45)' },
    ready:      { bg: 'oklch(90% 0.04 152)', text: 'oklch(38% 0.08 152)' },
    error:      { bg: 'oklch(93% 0.04 20)', text: 'oklch(38% 0.12 20)' },
  };
  const c = colors[status] ?? colors.idle;
  return (
    <span style={{
      background: c.bg, color: c.text,
      borderRadius: 999, padding: '3px 10px',
      fontSize: 11, fontWeight: 600, letterSpacing: '0.08em',
      textTransform: 'uppercase',
    }}>
      {status}
    </span>
  );
}

function VoiceRadio({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const voices = ['alloy', 'ash', 'ballad', 'coral', 'echo', 'sage', 'shimmer', 'verse', 'marin', 'cedar'];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: T.espressoLight }}>
        Voice
      </span>
      {voices.map((v) => (
        <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: T.espresso }}>
          <span style={{
            width: 16, height: 16, borderRadius: '50%',
            border: `1.5px solid ${value === v ? T.terra : T.creamBorder}`,
            background: value === v ? T.terra : T.white,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, transition: 'all 0.15s',
          }}>
            {value === v && <span style={{ width: 6, height: 6, borderRadius: '50%', background: T.white }} />}
          </span>
          <input type="radio" name="voice" value={v} checked={value === v} onChange={() => onChange(v)}
            style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }} />
          {v.charAt(0).toUpperCase() + v.slice(1)}
        </label>
      ))}
    </div>
  );
}

function TranscriptBubble({ entry, isStreaming }: { entry: TranscriptEntry; isStreaming: boolean }) {
  const isAi = entry.role === 'assistant';
  return (
    <div className="fadeIn" style={{
      display: 'flex', flexDirection: 'column', gap: 4,
      alignItems: isAi ? 'flex-start' : 'flex-end',
    }}>
      <span style={{
        fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase',
        color: isAi ? T.terra : T.espressoLight,
        paddingLeft: 4,
      }}>
        {isAi ? 'AI' : 'You'}
      </span>
      <div style={{
        maxWidth: '85%',
        background: isAi ? T.terraLight : T.creamMid,
        borderRadius: isAi ? '4px 14px 14px 14px' : '14px 14px 4px 14px',
        padding: '10px 14px',
        fontSize: 14, lineHeight: 1.55,
        color: T.espresso,
        fontStyle: isStreaming ? 'italic' : 'normal',
      }}>
        {entry.text}
        {isStreaming && (
          <span style={{
            display: 'inline-block', width: 2, height: 14,
            background: T.terra, marginLeft: 3, verticalAlign: 'middle',
            animation: 'pulse 1s ease-in-out infinite',
          }} />
        )}
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function TestInterviewPage() {
  const session = useRealtimeSession();
  const { status, orbState, micLevel, ended, error, transcript, streamingAiText } = session;

  const [voice, setVoice] = useState('marin');
  const [muted, setMuted] = useState(false);
  const [paused, setPaused] = useState(false);
  const [userInput, setUserInput] = useState('');
  const transcriptEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll transcript to bottom
  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [transcript, streamingAiText]);

  // Focus textarea when connection is ready
  useEffect(() => {
    if (status === 'ready') {
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [status]);

  // Cleanup on unmount
  useEffect(() => {
    return () => { session.stop(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConnect = useCallback(() => {
    session.start(voice);
  }, [session, voice]);

  const handleDisconnect = useCallback(() => {
    session.stop();
  }, [session]);

  const handleMute = useCallback(() => {
    const next = !muted;
    setMuted(next);
    session.mute(next);
  }, [muted, session]);

  const handlePause = useCallback(() => {
    const next = !paused;
    setPaused(next);
    session.pause(next);
  }, [paused, session]);

  const handleSend = useCallback(() => {
    const text = userInput.trim();
    if (!text || status !== 'ready') return;
    setUserInput('');
    session.sendText(text);
  }, [userInput, status, session]);

  const stateLabel =
    ended ? 'Interview complete'
    : orbState === 'speaking' ? 'AI speaking'
    : orbState === 'listening' ? 'Listening…'
    : orbState === 'thinking' ? 'Thinking…'
    : status === 'connecting' ? 'Connecting…'
    : 'Idle';

  const isConnected = status === 'ready';
  const canSend = isConnected && userInput.trim().length > 0;

  return (
    <div style={{ minHeight: '100vh', background: T.cream, display: 'flex', flexDirection: 'column' }}>

      {/* Top bar */}
      <div style={{
        borderBottom: `1px solid ${T.creamBorder}`,
        padding: '14px 28px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: T.white,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: T.terra, boxShadow: `0 0 0 3px oklch(62% 0.13 48 / 0.12)` }} />
            <span className="serif" style={{ fontSize: 17, color: T.espresso, letterSpacing: '-0.01em' }}>
              AI Interviewer
            </span>
          </div>
          <span style={{ color: T.creamBorder, fontSize: 18 }}>—</span>
          <span style={{ fontSize: 14, color: T.espressoMid }}>Test Interface</span>
        </div>
        <span style={{
          fontSize: 10, fontWeight: 700, letterSpacing: '0.12em',
          color: T.terra, border: `1.5px solid ${T.terra}`,
          borderRadius: 4, padding: '2px 7px', textTransform: 'uppercase',
        }}>
          DEV
        </span>
      </div>

      {/* Main content */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* ── Left panel ──────────────────────────────────────────────────── */}
        <div style={{
          width: 280, flexShrink: 0,
          borderRight: `1px solid ${T.creamBorder}`,
          display: 'flex', flexDirection: 'column',
          padding: '32px 24px', gap: 28,
          background: T.white,
        }}>
          {/* Orb + state */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
            <Orb
              state={orbState}
              size={160}
              micLevel={orbState === 'listening' ? micLevel : undefined}
            />
            <div style={{
              fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase',
              color: T.espressoLight, fontWeight: 500,
            }}>
              {stateLabel}
            </div>
          </div>

          {/* Status */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: T.espressoLight }}>
              Status
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <StatusBadge status={ended ? 'ended' : status} />
              {status === 'ready' && !ended && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: T.espressoLight }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: T.danger, animation: 'pulse 1.6s ease-in-out infinite' }} />
                  Recording
                </div>
              )}
            </div>
            {error && (
              <p style={{ fontSize: 12, color: T.danger, lineHeight: 1.4 }}>{error}</p>
            )}
          </div>

          {/* Voice selector */}
          <VoiceRadio
            value={voice}
            onChange={(v) => {
              setVoice(v);
              if (isConnected) {
                // Reconnect with new voice
                session.stop();
                setTimeout(() => session.start(v), 100);
              }
            }}
          />

          {/* Mute / Pause toggles (when connected) */}
          {isConnected && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}>
                <span style={{
                  width: 36, height: 20, borderRadius: 10,
                  background: muted ? T.espresso : T.creamBorder,
                  position: 'relative', transition: 'background 0.2s', flexShrink: 0,
                }}>
                  <span style={{
                    position: 'absolute', top: 2, left: muted ? 18 : 2,
                    width: 16, height: 16, borderRadius: '50%',
                    background: T.white, transition: 'left 0.2s',
                    boxShadow: '0 1px 3px oklch(18% 0.018 60 / 0.2)',
                  }} />
                </span>
                <input type="checkbox" checked={muted} onChange={handleMute} style={{ position: 'absolute', opacity: 0 }} />
                <span style={{ fontSize: 13, color: T.espresso }}>Mute mic</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}>
                <span style={{
                  width: 36, height: 20, borderRadius: 10,
                  background: paused ? T.terra : T.creamBorder,
                  position: 'relative', transition: 'background 0.2s', flexShrink: 0,
                }}>
                  <span style={{
                    position: 'absolute', top: 2, left: paused ? 18 : 2,
                    width: 16, height: 16, borderRadius: '50%',
                    background: T.white, transition: 'left 0.2s',
                    boxShadow: '0 1px 3px oklch(18% 0.018 60 / 0.2)',
                  }} />
                </span>
                <input type="checkbox" checked={paused} onChange={handlePause} style={{ position: 'absolute', opacity: 0 }} />
                <span style={{ fontSize: 13, color: T.espresso }}>Pause VAD</span>
              </label>
            </div>
          )}

          {/* Connect / Disconnect */}
          <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {!isConnected ? (
              <button
                onClick={handleConnect}
                disabled={status === 'connecting'}
                style={{
                  background: status === 'connecting' ? T.creamMid : T.terra,
                  color: status === 'connecting' ? T.espressoLight : T.white,
                  border: 'none', borderRadius: 10,
                  padding: '12px', fontSize: 14, fontWeight: 500,
                  cursor: status === 'connecting' ? 'default' : 'pointer',
                  fontFamily: 'inherit', width: '100%',
                  boxShadow: status === 'connecting' ? 'none' : '0 1px 2px oklch(48% 0.14 45 / 0.2), 0 4px 16px oklch(48% 0.14 45 / 0.18)',
                  transition: 'all 0.15s',
                }}
              >
                {status === 'connecting' ? 'Connecting…' : 'Connect'}
              </button>
            ) : (
              <button
                onClick={handleDisconnect}
                style={{
                  background: 'transparent', color: T.espressoMid,
                  border: `1px solid ${T.creamBorder}`, borderRadius: 10,
                  padding: '12px', fontSize: 14, fontWeight: 500,
                  cursor: 'pointer', fontFamily: 'inherit', width: '100%',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = T.espressoLight; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = T.creamBorder; }}
              >
                Disconnect
              </button>
            )}
          </div>
        </div>

        {/* ── Right panel ─────────────────────────────────────────────────── */}
        <div style={{
          flex: 1, display: 'flex', flexDirection: 'column',
          overflow: 'hidden', minWidth: 0,
        }}>
          {/* Transcript header */}
          <div style={{
            padding: '16px 24px',
            borderBottom: `1px solid ${T.creamBorder}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: T.espresso }}>
              Conversation
            </span>
            {transcript.length > 0 && (
              <span style={{ fontSize: 11, color: T.espressoLight }}>
                {transcript.length} turn{transcript.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>

          {/* Transcript scroll area */}
          <div style={{
            flex: 1, overflowY: 'auto',
            padding: '20px 24px',
            display: 'flex', flexDirection: 'column', gap: 16,
          }}>
            {transcript.length === 0 ? (
              <div style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <p style={{ fontSize: 14, color: T.espressoLight, textAlign: 'center' }}>
                  {isConnected
                    ? 'The AI will start speaking shortly…'
                    : 'Connect to start the interview'}
                </p>
              </div>
            ) : (
              transcript.map((entry) => (
                <TranscriptBubble key={entry.id} entry={entry} isStreaming={false} />
              ))
            )}
            {streamingAiText && (
              <TranscriptBubble
                key="streaming"
                entry={{ id: 'streaming', role: 'assistant', text: streamingAiText, timestamp: '' }}
                isStreaming
              />
            )}
            <div ref={transcriptEndRef} />
          </div>

          {/* Text input */}
          <div style={{
            borderTop: `1px solid ${T.creamBorder}`,
            padding: '16px 24px',
            display: 'flex', gap: 10, alignItems: 'flex-end',
          }}>
            <textarea
              ref={textareaRef}
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder={isConnected ? 'Type a message, or speak… (Enter to send)' : 'Connect first to send a message'}
              disabled={!isConnected || ended}
              rows={2}
              style={{
                flex: 1,
                border: `1.5px solid ${T.creamBorder}`,
                borderRadius: 12, padding: '10px 14px',
                fontFamily: 'inherit', fontSize: 14, lineHeight: 1.5,
                color: T.espresso, background: isConnected && !ended ? T.white : T.creamMid,
                resize: 'none', outline: 'none',
                transition: 'border-color 0.15s',
                opacity: isConnected && !ended ? 1 : 0.5,
              }}
              onFocus={(e) => { e.currentTarget.style.borderColor = T.terra; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = T.creamBorder; }}
            />
            <button
              onClick={handleSend}
              disabled={!canSend}
              style={{
                background: canSend ? T.terra : T.creamMid,
                color: canSend ? T.white : T.espressoLight,
                border: 'none', borderRadius: 10,
                padding: '10px 18px', fontSize: 14, fontWeight: 500,
                cursor: canSend ? 'pointer' : 'default',
                fontFamily: 'inherit', flexShrink: 0,
                transition: 'all 0.15s', alignSelf: 'flex-end',
                height: 42,
              }}
            >
              Send
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
